import { relativeTime } from '../util.js'

/**
 * Crawl health, not decoration. A stale lastRunAt or a non-empty failure list is
 * the difference between "no Gold Wings matched today" and "the crawler has been
 * broken for a week" — which look identical from an empty list alone.
 *
 * criteriaFallback and notesReadFailed are the quiet ones: the crawl succeeded
 * and the counts look normal, but it ran against criteria.json instead of the
 * operator's settings, or without dismissal suppression. Both are invisible
 * anywhere else in the app.
 */
export default function StatusBar({ meta, counts }) {
  const failures = meta?.sourcesFailed?.length ?? 0
  const extractFailures = meta?.extractFailures?.length ?? 0
  const truncated = meta?.truncatedSources?.length ?? 0
  const paginationIncomplete = meta?.paginationIncomplete?.length ?? 0

  const lastRun = meta?.lastRunAt
  const ageHours = lastRun ? (Date.now() - new Date(lastRun).getTime()) / 3.6e6 : Infinity
  // The crawl is daily; past ~36h something is wrong with the timer or the box.
  const stale = ageHours > 36

  const problems = []
  if (stale) problems.push(`last crawl ${relativeTime(lastRun)}`)
  if (failures) problems.push(`${failures} source${failures > 1 ? 's' : ''} failed`)
  if (extractFailures) problems.push(`${extractFailures} extractor failure${extractFailures > 1 ? 's' : ''}`)
  if (truncated) problems.push(`${truncated} source${truncated > 1 ? 's' : ''} truncated`)
  if (paginationIncomplete) problems.push(`${paginationIncomplete} source${paginationIncomplete > 1 ? 's' : ''} pagination incomplete`)
  // Both are null on a healthy run, so check truthiness, not presence.
  if (meta?.criteriaFallback) problems.push(`search settings rejected — ${meta.criteriaFallback}`)
  if (meta?.notesReadFailed) problems.push('notes unavailable last crawl — dismissals were not applied')

  // Phase 2b. enrichBlocked means MotoHunt served the VPS a challenge;
  // enrichUnparseable means detail pages had neither the live markers nor the
  // "no longer available" marker, which is the signature of a markup change.
  // Both are otherwise invisible — the crawler runs unattended and the canary
  // only runs on deploy.
  //
  // enrichUnparseable and enrichFailures are both a SENTENCE or null, built by
  // the crawler (crawler/lib/enrich.js:171-183), and both go through verbatim:
  // they already carry the ratio and the diagnosis, which is more than this bar
  // could say about them. Reading enrichUnparseable as a count instead —
  // interpolating it into a sentence of this file's own and pluralising it —
  // rendered the crawler's whole sentence followed by a fragment of one.
  if (meta?.enrichBlocked) problems.push('enrichment blocked — MotoHunt challenged the crawler')
  if (meta?.enrichUnparseable) problems.push(meta.enrichUnparseable)
  if (meta?.enrichFailures) problems.push(meta.enrichFailures)

  return (
    <div className={`status ${problems.length ? 'bad' : 'ok'}`}>
      <div className="status-line">
        <span className="dot" aria-hidden="true" />
        <span>
          {meta?.lastRunAt ? `Crawled ${relativeTime(meta.lastRunAt)}` : 'No crawl recorded yet'}
          {meta?.sourcesCrawled ? ` · ${meta.sourcesCrawled} sources` : ''}
        </span>
      </div>
      <div className="status-counts">
        <strong>{counts.matches}</strong> match
        {counts.soft > 0 && (
          <>
            {' · '}
            <strong>{counts.soft}</strong> close
          </>
        )}
        {' · '}
        <strong>{counts.tracked}</strong> tracked
        {/* The per-run enrichment cap doing its job. Not a failure, but not
            nothing either: deferred listings are never re-offered, so a
            persistently non-zero value means bikes stay unenriched for good. */}
        {meta?.enrichSkipped > 0 && ` · ${meta.enrichSkipped} deferred`}
      </div>
      {problems.length > 0 && <p className="status-problems">{problems.join(' · ')}</p>}
    </div>
  )
}
